import { apiClient } from './api'

// Types for metrics data
export type MetricDataPoint = {
  time: string
  measurement: string
  field: string
  value: number
  tags: Record<string, any>
}

export type MetricsResponse = {
  success: boolean
  data: MetricDataPoint[]
  count: number
  time_range: string
  filters: Record<string, any>
}

export type ClusterSummary = {
  total_nodes: number
  avg_cpu_usage: number
  avg_memory_usage: number
  metrics_count: number
  timestamp: string
}

export type HostsResponse = {
  success: boolean
  hosts: string[]
  count: number
}

export type HealthCheckResponse = {
  connected: boolean
  url: string
  organization: string
  bucket: string
  last_check: string
}

export type MetricsQueryRequest = {
  time_range?: string
  cluster_name?: string
  node_name?: string
  vm_id?: string
  measurement?: string
}

export type CustomMetricRequest = {
  measurement: string
  fields: Record<string, any>
  tags?: Record<string, string>
  timestamp?: string
}

export type CustomMetricResponse = {
  success: boolean
  message: string
  measurement: string
}

// Dashboard-specific types
export type DashboardOverview = {
  organizations: {
    total: number
    by_type: {
      cloud_provider: number
      reseller: number
      end_customer: number
    }
  }
  users: {
    total: number
    active: number
    inactive: number
  }
  resources: {
    total: number
    active: number
    inactive: number
  }
  capacity?: {
    cpu: {
      total: number
      used: number
      usage_percent: number
    }
    ram: {
      total: number
      used: number
      usage_percent: number
    }
    storage: {
      total: number
      used: number
      usage_percent: number
    }
  }
}

export type GrowthData = {
  organizations: {
    date: string
    total: number
  }[]
  users: {
    date: string
    total: number
  }[]
  resources: {
    date: string
    total: number
  }[]
}

export type TopOrganization = {
  id: string
  name: string
  type: string
  value: number
  metric: string
}

export type SystemHealth = {
  status: 'healthy' | 'warning' | 'critical'
  metrics: {
    error_rate: number
    response_time: number
    uptime: number
  }
  services: Record<
    string,
    {
      status: 'healthy' | 'warning' | 'critical'
      message: string
    }
  >
}

export type RecentActivity = {
  title: string
  description: string
  icon: string
  timestamp: string
}

export const metricsApi = {
  // Health check
  async getHealth(): Promise<HealthCheckResponse> {
    return apiClient.get<HealthCheckResponse>('/api/v1/metrics/health')
  },

  // Get available hosts
  async getHosts(): Promise<HostsResponse> {
    return apiClient.get<HostsResponse>('/api/v1/metrics/hosts')
  },

  // Get cluster summary
  async getClusterSummary(timeRange: string = '-1h'): Promise<ClusterSummary> {
    return apiClient.get<ClusterSummary>(
      `/api/v1/metrics/cluster/summary?time_range=${encodeURIComponent(timeRange)}`,
    )
  },

  // Generic query
  async queryMetrics(request: MetricsQueryRequest): Promise<MetricsResponse> {
    return apiClient.post<MetricsResponse>('/api/v1/metrics/query', request)
  },

  // CPU metrics
  async getCpuMetrics(
    timeRange: string = '-1h',
    clusterName?: string,
    nodeName?: string,
  ): Promise<MetricsResponse> {
    const params = new URLSearchParams({ time_range: timeRange })
    if (clusterName) params.append('cluster_name', clusterName)
    if (nodeName) params.append('node_name', nodeName)

    return apiClient.get<MetricsResponse>(`/api/v1/metrics/cpu?${params}`)
  },

  // Memory metrics
  async getMemoryMetrics(
    timeRange: string = '-1h',
    clusterName?: string,
    nodeName?: string,
  ): Promise<MetricsResponse> {
    const params = new URLSearchParams({ time_range: timeRange })
    if (clusterName) params.append('cluster_name', clusterName)
    if (nodeName) params.append('node_name', nodeName)

    return apiClient.get<MetricsResponse>(`/api/v1/metrics/memory?${params}`)
  },

  // Network metrics
  async getNetworkMetrics(
    timeRange: string = '-1h',
    clusterName?: string,
    nodeName?: string,
  ): Promise<MetricsResponse> {
    const params = new URLSearchParams({ time_range: timeRange })
    if (clusterName) params.append('cluster_name', clusterName)
    if (nodeName) params.append('node_name', nodeName)

    return apiClient.get<MetricsResponse>(`/api/v1/metrics/network?${params}`)
  },

  // Disk metrics
  async getDiskMetrics(
    timeRange: string = '-1h',
    clusterName?: string,
    nodeName?: string,
  ): Promise<MetricsResponse> {
    const params = new URLSearchParams({ time_range: timeRange })
    if (clusterName) params.append('cluster_name', clusterName)
    if (nodeName) params.append('node_name', nodeName)

    return apiClient.get<MetricsResponse>(`/api/v1/metrics/disk?${params}`)
  },

  // Write custom metric
  async writeCustomMetric(request: CustomMetricRequest): Promise<CustomMetricResponse> {
    return apiClient.post<CustomMetricResponse>('/api/v1/metrics/custom', request)
  },

  // Utility functions
  formatTimeRange(range: string): string {
    const timeRangeLabels: Record<string, string> = {
      '-5m': 'Últimos 5 minutos',
      '-15m': 'Últimos 15 minutos',
      '-1h': 'Última hora',
      '-6h': 'Últimas 6 horas',
      '-24h': 'Últimas 24 horas',
      '-7d': 'Últimos 7 dias',
      '-30d': 'Últimos 30 dias',
    }
    return timeRangeLabels[range] || range
  },

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  },

  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`
  },

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  },

  // Dashboard-specific functions
  async getOverview(): Promise<DashboardOverview> {
    return apiClient.get<DashboardOverview>('/api/v1/admin/metrics/overview')
  },

  async getGrowth(days: number): Promise<GrowthData> {
    return apiClient.get<GrowthData>(`/api/v1/admin/metrics/growth?days=${days}`)
  },

  async getTopOrganizations(
    limit: number = 10,
    metric: string = 'users',
  ): Promise<TopOrganization[]> {
    return apiClient.get<TopOrganization[]>(
      `/api/v1/admin/metrics/top-organizations?limit=${limit}&metric=${metric}`,
    )
  },

  async getSystemHealth(): Promise<SystemHealth> {
    return apiClient.get<SystemHealth>('/api/v1/admin/metrics/system-health')
  },

  async getRecentActivities(limit: number = 10): Promise<RecentActivity[]> {
    return apiClient.get<RecentActivity[]>(`/api/v1/admin/metrics/recent-activities?limit=${limit}`)
  },
}
